/**
 * RFRB Gamepad tester — HTML5 Gamepad API
 * Marks pressed buttons as tested (lime). Detects stick drift vs OK.
 */
if (!customElements.get('gamepad-tester')) {
  class GamepadTester extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.activeSlot = 0;
      this.raf = 0;
      this.testedButtons = new Set();
      this.triggerMax = { 6: 0, 7: 0 };
      this.stickState = {
        left: this._freshStick(),
        right: this._freshStick(),
      };

      this.driftThreshold = Math.max(
        0.05,
        Math.min(0.3, (Number(this.dataset.driftThreshold) || 12) / 100)
      );

      this.i18n = {
        waiting: this.dataset.i18nWaiting || 'Väntar på handkontroll…',
        connected: this.dataset.i18nConnected || 'Ansluten',
        disconnected: this.dataset.i18nDisconnected || 'Frånkopplad',
        pressToStart: this.dataset.i18nPressToStart || 'Anslut handkontrollen och tryck på en knapp…',
        slotEmpty: this.dataset.i18nSlotEmpty || 'Ingen upptäckt',
        progress: this.dataset.i18nProgress || '{{tested}} / {{total}} testade',
        buttonsDone: this.dataset.i18nButtonsDone || 'Knappar: {{tested}} / {{total}}',
        sticksDone: this.dataset.i18nSticksDone || 'Stickor: {{ok}} / 2 OK',
        allOk: this.dataset.i18nAllOk || 'Alla knappar och stickor godkända',
        stickPending: this.dataset.i18nStickPending || 'Ej testad',
        stickMove: this.dataset.i18nStickMove || 'Rör stickan',
        stickOk: this.dataset.i18nStickOk || 'Felfri',
        stickDrift: this.dataset.i18nStickDrift || 'Stickdrift',
        stickBounce: this.dataset.i18nStickBounce || 'Studsar (varning)',
        touchpadPending: this.dataset.i18nTouchpadPending || 'Ej testad',
        touchpadMove: this.dataset.i18nTouchpadMove || 'Dra över ytan',
        touchpadOk: this.dataset.i18nTouchpadOk || 'Touchpad OK',
        touchpadClickOnly: this.dataset.i18nTouchpadClickOnly || 'Klick OK · yta saknas',
        touchpadUnsupported: this.dataset.i18nTouchpadUnsupported || 'Yta ej tillgänglig',
        touchpadFingers: this.dataset.i18nTouchpadFingers || '{{count}} finger · täckning {{pct}}%',
        touchpadHid: this.dataset.i18nTouchpadHid || 'Aktivera touchpad (USB)',
        touchpadHidActive: this.dataset.i18nTouchpadHidActive || 'Touchpad via USB aktiv',
        touchpadHidError: this.dataset.i18nTouchpadHidError || 'Kunde inte öppna DualSense via USB.',
        touchpadHidUnsupported: this.dataset.i18nTouchpadHidUnsupported || 'WebHID saknas – använd Chrome eller Edge.',
        vibrateUnsupported: this.dataset.i18nVibrateUnsupported || 'Vibration stöds inte i den här webbläsaren.',
        reset: this.dataset.i18nReset || 'Nollställ test',
      };

      this.buttonDefs = [
        { index: 0, label: 'A / ✕' },
        { index: 1, label: 'B / ○' },
        { index: 2, label: 'X / □' },
        { index: 3, label: 'Y / △' },
        { index: 4, label: 'LB / L1' },
        { index: 5, label: 'RB / R1' },
        { index: 6, label: 'LT / L2' },
        { index: 7, label: 'RT / R2' },
        { index: 8, label: 'Select / Create' },
        { index: 9, label: 'Start / Options' },
        { index: 10, label: 'L3' },
        { index: 11, label: 'R3' },
        { index: 12, label: 'D-pad ↑' },
        { index: 13, label: 'D-pad ↓' },
        { index: 14, label: 'D-pad ←' },
        { index: 15, label: 'D-pad →' },
        { index: 16, label: 'Home / PS' },
        { index: 17, label: 'Touchpad-klick' },
      ];

      this.touchpadState = this._freshTouchpad();

      this.els = {
        idle: this.querySelector('[data-idle]'),
        idleText: this.querySelector('[data-idle-text]'),
        active: this.querySelector('[data-active]'),
        deviceName: this.querySelector('[data-device-name]'),
        deviceModel: this.querySelector('[data-device-model]'),
        connectionStatus: this.querySelector('[data-connection-status]'),
        progressFill: this.querySelector('[data-progress-fill]'),
        progressText: this.querySelector('[data-progress-text]'),
        checklist: this.querySelector('[data-checklist]'),
        vibrateNote: this.querySelector('[data-vibrate-note]'),
        slots: [...this.querySelectorAll('[data-slot]')],
        slotLabels: [...this.querySelectorAll('[data-slot-label]')],
        btnNodes: [...this.querySelectorAll('[data-btn]')],
        stickKnobs: {
          left: this.querySelector('[data-stick-knob="left"]'),
          right: this.querySelector('[data-stick-knob="right"]'),
        },
        stickCards: {
          left: this.querySelector('[data-stick="left"]'),
          right: this.querySelector('[data-stick="right"]'),
        },
        stickBadges: {
          left: this.querySelector('[data-stick-badge="left"]'),
          right: this.querySelector('[data-stick-badge="right"]'),
        },
        stickXy: {
          left: this.querySelector('[data-stick-xy="left"]'),
          right: this.querySelector('[data-stick-xy="right"]'),
        },
        canvases: {
          left: this.querySelector('[data-stick-canvas="left"]'),
          right: this.querySelector('[data-stick-canvas="right"]'),
        },
        touchpadCard: this.querySelector('[data-touchpad-card]'),
        touchpadBadge: this.querySelector('[data-touchpad-badge]'),
        touchpadCanvas: this.querySelector('[data-touchpad-canvas]'),
        touchpadInfo: this.querySelector('[data-touchpad-info]'),
        touchpadNote: this.querySelector('[data-touchpad-note]'),
        touchpadHidBtn: this.querySelector('[data-touchpad-hid]'),
        touchpadClickCheck: this.querySelector('[data-touchpad-click-check]'),
        touchpadSurfaceCheck: this.querySelector('[data-touchpad-surface-check]'),
        hidDebug: this.querySelector('[data-hid-debug]'),
        serialInput: this.querySelector('[data-serial-input]'),
        serialApply: this.querySelector('[data-serial-apply]'),
        touchDots: [...this.querySelectorAll('[data-touch-dot]')],
      };

      this.hid = {
        device: null,
        click: false,
        fingers: [],
        active: false,
        reportCount: 0,
        lastReportId: null,
        wireless: false,
      };
      this.deviceIdentity = null;

      this.trails = {
        left: [],
        right: [],
        touchpad: [],
      };

      this._buildChecklist();
      this._bindUi();
      this._drawStickPad('left', 0, 0);
      this._drawStickPad('right', 0, 0);
      this._drawTouchpad([]);
      this._updateSlotLabels();
      this._setIdle(true);

      window.addEventListener('gamepadconnected', this._onConnect);
      window.addEventListener('gamepaddisconnected', this._onDisconnect);
      this._startLoop();
    }

    disconnectedCallback() {
      this._stopLoop();
      window.removeEventListener('gamepadconnected', this._onConnect);
      window.removeEventListener('gamepaddisconnected', this._onDisconnect);
      this._closeHid();
    }

    _freshStick() {
      return {
        status: 'pending',
        maxMag: 0,
        restOffMs: 0,
        lastX: 0,
        lastY: 0,
        rangeOk: false,
        holdDirMs: 0,
        jitterMs: 0,
        bounceActive: false,
        _angle: null,
      };
    }

    _freshTouchpad() {
      return {
        surfaceSeen: false,
        surfaceOk: false,
        cells: new Set(),
        waitMs: 0,
        lastFingers: [],
      };
    }

    _onConnect = () => {
      this._updateSlotLabels();
    };

    _onDisconnect = () => {
      this._updateSlotLabels();
    };

    _bindUi() {
      this.els.slots.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.activeSlot = Number(btn.dataset.slot) || 0;
          this.els.slots.forEach((b) => {
            const on = b === btn;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          this._resetTest(false);
        });
      });

      const resetBtn = this.querySelector('[data-reset]');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => this._resetTest(true));
      }

      this.querySelectorAll('[data-vibrate]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const mode = btn.getAttribute('data-vibrate');
          this._vibrate(mode === 'strong' ? 1 : 0.35);
        });
      });

      if (this.els.touchpadHidBtn) {
        const supported = typeof navigator !== 'undefined' && navigator.hid;
        if (!supported) {
          this.els.touchpadHidBtn.disabled = true;
          this.els.touchpadHidBtn.title = this.i18n.touchpadHidUnsupported;
        }
        this.els.touchpadHidBtn.addEventListener('click', () => this._connectHid());
      }

      if (this.els.serialApply && this.els.serialInput) {
        const apply = () => this._applySerial(this.els.serialInput.value);
        this.els.serialApply.addEventListener('click', apply);
        this.els.serialInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            apply();
          }
        });
      }
    }

    _buildChecklist() {
      if (!this.els.checklist) return;
      this.els.checklist.innerHTML = '';
      this.buttonDefs.forEach((def) => {
        const li = document.createElement('li');
        li.className = 'gamepad-tester__check';
        li.dataset.check = String(def.index);
        li.innerHTML = `<span class="gamepad-tester__check-dot" aria-hidden="true"></span><span>${def.label}</span>`;
        this.els.checklist.appendChild(li);
      });
    }

    _startLoop() {
      const tick = (now) => {
        this._frame(now || performance.now());
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    _stopLoop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    _pads() {
      return navigator.getGamepads ? [...navigator.getGamepads()] : [];
    }

    _updateSlotLabels() {
      const pads = this._pads();
      this.els.slotLabels.forEach((label) => {
        const i = Number(label.getAttribute('data-slot-label'));
        const pad = pads[i];
        const slotBtn = this.els.slots[i];
        if (pad) {
          label.textContent = this._shortName(pad.id);
          slotBtn?.classList.add('is-connected');
        } else {
          label.textContent = this.i18n.slotEmpty;
          slotBtn?.classList.remove('is-connected');
        }
      });
    }

    _shortName(id) {
      if (!id) return 'Gamepad';
      return id.replace(/\s*\(.*?\)\s*/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 42);
    }

    _setIdle(idle) {
      if (this.els.idle) this.els.idle.hidden = !idle;
      if (this.els.active) this.els.active.hidden = idle;
    }

    _resetTest(clearTrails) {
      this.testedButtons = new Set();
      this.triggerMax = { 6: 0, 7: 0 };
      this.stickState = {
        left: this._freshStick(),
        right: this._freshStick(),
      };
      this.touchpadState = this._freshTouchpad();
      if (clearTrails) {
        this.trails.left = [];
        this.trails.right = [];
        this.trails.touchpad = [];
      }
      this.els.btnNodes.forEach((node) => {
        node.classList.remove(
          'is-active',
          'is-tested',
          'is-partial',
          'is-trigger-full',
          'is-trigger-near',
          'is-trigger-low'
        );
      });
      this.querySelectorAll('[data-trigger-pct]').forEach((el) => {
        el.textContent = '0%';
      });
      this.querySelectorAll('[data-check]').forEach((el) => {
        el.classList.remove('is-tested', 'is-active', 'is-trigger-near', 'is-trigger-low');
      });
      this._setStickUi('left', 'pending');
      this._setStickUi('right', 'pending');
      this._setTouchpadUi('pending', []);
      this._drawStickPad('left', 0, 0);
      this._drawStickPad('right', 0, 0);
      this._drawTouchpad([]);
      this._updateProgress();
    }

    _frame(now) {
      if (!this._lastSlotUpdate || now - this._lastSlotUpdate > 500) {
        this._updateSlotLabels();
        this._lastSlotUpdate = now;
      }

      const pad = this._pads()[this.activeSlot];
      if (!pad) {
        this._setIdle(true);
        if (this.els.idleText) this.els.idleText.textContent = this.i18n.pressToStart;
        return;
      }

      this._setIdle(false);
      this._updateDeviceInfo(pad);

      const dt = this._lastFrame ? Math.min(50, now - this._lastFrame) : 16;
      this._lastFrame = now;

      this._updateButtons(pad);
      this._updateStick('left', pad.axes[0] || 0, pad.axes[1] || 0, dt, now);
      this._updateStick('right', pad.axes[2] || 0, pad.axes[3] || 0, dt, now);
      this._updateTouchpad(pad, dt, now);
      this._updateProgress();
    }

    _parseVendorProduct(id) {
      const text = String(id || '');
      const vendor = (text.match(/Vendor:\s*([0-9a-fA-F]+)/i) || [])[1];
      const product = (text.match(/Product:\s*([0-9a-fA-F]+)/i) || [])[1];
      return {
        vendorId: vendor ? parseInt(vendor, 16) : null,
        productId: product ? parseInt(product, 16) : null,
      };
    }

    _sonyModelFromProductId(productId) {
      switch (productId) {
        case 0x0ce6:
          return { family: 'DualSense', variant: 'Standard' };
        case 0x0df2:
          return { family: 'DualSense Edge', variant: 'Edge' };
        case 0x0ce4:
          return { family: 'DualSense', variant: 'Standard' };
        case 0x09cc:
        case 0x05c4:
          return { family: 'DualShock 4', variant: 'DS4' };
        default:
          return null;
      }
    }

    _bdmLabel(board) {
      const map = {
        'BDM-010': 'V1 (BDM-010)',
        'BDM-020': 'V2 (BDM-020)',
        'BDM-030': 'V3 (BDM-030)',
        'BDM-040': 'V4 (BDM-040)',
        'BDM-050': 'V5 (BDM-050)',
      };
      return map[board] || board;
    }

    _formatFwVersion(raw) {
      // 0xAABBCCCC → AA.BB.CCCC
      const aa = (raw >>> 24) & 0xff;
      const bb = (raw >>> 16) & 0xff;
      const cccc = raw & 0xffff;
      return `${aa}.${bb}.${cccc}`;
    }

    _updateDeviceInfo(pad) {
      const fromId = this._parseVendorProduct(pad.id);
      const hidPid = this.hid.device ? this.hid.device.productId : null;
      const productId = hidPid || fromId.productId;
      const model = this._sonyModelFromProductId(productId);
      const identity = this.deviceIdentity;

      if (this.els.deviceName) {
        this.els.deviceName.textContent = pad.id || 'Gamepad';
      }

      if (this.els.deviceModel) {
        if (identity && identity.summary) {
          this.els.deviceModel.textContent = identity.summary;
        } else if (model) {
          this.els.deviceModel.textContent =
            productId != null
              ? `${model.family} · PID ${productId.toString(16).padStart(4, '0')}`
              : model.family;
        } else if (this._looksLikeSony(pad)) {
          this.els.deviceModel.textContent = 'Sony-handkontroll (okänd modell)';
        } else {
          this.els.deviceModel.textContent = '';
        }
      }

      if (this.els.connectionStatus) {
        const bits = [
          this.i18n.connected,
          (pad.mapping || 'standard').toUpperCase(),
        ];
        if (identity && identity.firmware) bits.push(`FW ${identity.firmware}`);
        if (this.hid.active) bits.push('WebHID');
        this.els.connectionStatus.textContent = bits.join(' · ');
      }
    }

    _buttonValue(btn) {
      if (typeof btn === 'number') return btn;
      if (!btn) return 0;
      return typeof btn.value === 'number' ? btn.value : btn.pressed ? 1 : 0;
    }

    _triggerLevel(maxPct) {
      if (maxPct >= 100) return 'full';
      if (maxPct >= 97) return 'near';
      if (maxPct > 0) return 'low';
      return null;
    }

    _updateButtons(pad) {
      const max = Math.max(pad.buttons.length, 18);

      for (let i = 0; i < max; i++) {
        const value = this._buttonValue(pad.buttons[i]);
        const isTrigger = i === 6 || i === 7;
        const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
        const pressed = isTrigger ? value > 0.02 : value > 0.12;

        if (isTrigger) {
          this.triggerMax[i] = Math.max(this.triggerMax[i] || 0, value);
          if (pressed) this.testedButtons.add(i);
        } else if (pressed) {
          this.testedButtons.add(i);
        }

        this.els.btnNodes
          .filter((n) => Number(n.dataset.btn) === i)
          .forEach((node) => {
            node.classList.toggle('is-active', pressed);
            node.classList.toggle('is-tested', this.testedButtons.has(i));

            if (isTrigger) {
              const maxVal = this.triggerMax[i] || 0;
              const maxPct = Math.round(Math.max(0, Math.min(1, maxVal)) * 100);
              const level = this._triggerLevel(maxPct);
              node.classList.toggle('is-trigger-full', level === 'full');
              node.classList.toggle('is-trigger-near', level === 'near');
              node.classList.toggle('is-trigger-low', level === 'low');
              node.classList.remove('is-partial');

              const pctEl =
                node.querySelector('[data-trigger-pct]') ||
                this.querySelector(`[data-trigger-pct="${i}"]`);
              if (pctEl) {
                // Live % while pressed, otherwise show best reached
                pctEl.textContent = pressed ? `${pct}%` : `${maxPct}%`;
              }
            }
          });

        const check = this.querySelector(`[data-check="${i}"]`);
        if (check) {
          check.classList.toggle('is-active', pressed);
          check.classList.toggle('is-tested', this.testedButtons.has(i));
          if (isTrigger) {
            const label = check.querySelector('span:last-child');
            const maxPct = Math.round((this.triggerMax[i] || 0) * 100);
            const base = i === 6 ? 'LT / L2' : 'RT / R2';
            const level = this._triggerLevel(maxPct);
            if (label) {
              label.textContent = pressed
                ? `${base} · ${pct}% (max ${maxPct}%)`
                : maxPct > 0
                  ? `${base} · max ${maxPct}%`
                  : base;
            }
            check.classList.toggle('is-trigger-full', level === 'full');
            check.classList.toggle('is-trigger-near', level === 'near');
            check.classList.toggle('is-trigger-low', level === 'low');
          }
        }
      }

      this.buttonDefs.forEach((def) => {
        const check = this.querySelector(`[data-check="${def.index}"]`);
        if (!check) return;
        if (def.index === 17) {
          check.hidden = !(pad.buttons.length > 17 || this.testedButtons.has(17) || this._looksLikeSony(pad));
          return;
        }
        if (def.index === 16) {
          check.hidden = !(def.index < pad.buttons.length || this.testedButtons.has(def.index));
        }
      });

      if (this.els.touchpadClickCheck) {
        const clickOn = this.testedButtons.has(17);
        const clickActive = this._buttonValue(pad.buttons[17]) > 0.12;
        this.els.touchpadClickCheck.classList.toggle('is-tested', clickOn);
        this.els.touchpadClickCheck.classList.toggle('is-active', clickActive);
      }
    }

    _looksLikeSony(pad) {
      const id = (pad.id || '').toLowerCase();
      return (
        id.includes('dualsense') ||
        id.includes('dualshock') ||
        id.includes('wireless controller') ||
        id.includes('054c') ||
        id.includes('0ce6') ||
        id.includes('09cc') ||
        id.includes('05c4') ||
        id.includes('0df2')
      );
    }

    _touchpadClickPressed(pad) {
      if (this.hid.active && this.hid.click) return true;
      if (!pad || !pad.buttons) return false;
      // Chromium: usually 17; some builds map mute/touchpad around 17–18
      for (let i = 17; i < Math.min(pad.buttons.length, 20); i++) {
        if (this._buttonValue(pad.buttons[i]) > 0.12) return true;
      }
      return false;
    }

    _readTouchFingers(pad) {
      if (this.hid.active && (this.hid.fingers.length || this.hid.click)) {
        return { fingers: this.hid.fingers.slice(), source: 'webhid' };
      }

      const fingers = [];
      const events = pad.touches || pad.touchEvents || null;
      const btn17 = pad.buttons && pad.buttons[17];
      const capacitive = Boolean(btn17 && btn17.touched);

      if (events && events.length) {
        for (let i = 0; i < Math.min(events.length, 2); i++) {
          const t = events[i];
          if (!t) continue;
          const pos = t.position || t;
          let x = typeof pos.x === 'number' ? pos.x : null;
          let y = typeof pos.y === 'number' ? pos.y : null;
          if (x == null || y == null) continue;
          if (x >= 0 && x <= 1 && y >= 0 && y <= 1) {
            x = x * 2 - 1;
            y = y * 2 - 1;
          }
          fingers.push({ x, y, id: t.touchId ?? i });
        }
        if (fingers.length) return { fingers, source: 'touchEvents' };
      }

      if (pad.axes && pad.axes.length >= 6) {
        const pairs = [
          [pad.axes[4], pad.axes[5]],
          pad.axes.length >= 8 ? [pad.axes[6], pad.axes[7]] : null,
        ];
        pairs.forEach((pair, i) => {
          if (!pair) return;
          let [x, y] = pair;
          if (typeof x !== 'number' || typeof y !== 'number') return;
          if (!Number.isFinite(x) || !Number.isFinite(y)) return;
          const looks01 = x >= 0 && x <= 1 && y >= 0 && y <= 1;
          if (looks01) {
            if (x === 0 && y === 0 && !capacitive) return;
            fingers.push({ x: x * 2 - 1, y: y * 2 - 1, id: i });
            return;
          }
          if (!capacitive && Math.hypot(x, y) < 0.02) return;
          fingers.push({ x, y, id: i });
        });
        if (fingers.length) return { fingers, source: 'axes' };
      }

      if (capacitive) {
        fingers.push({ x: 0, y: 0, id: 0, approximate: true });
        return { fingers, source: 'touched' };
      }

      return { fingers: [], source: null };
    }

    _updateTouchpad(pad, dt, now) {
      const state = this.touchpadState;
      const { fingers, source } = this._readTouchFingers(pad);
      const clickActive = this._touchpadClickPressed(pad);
      if (clickActive) {
        this.testedButtons.add(17);
        this.els.btnNodes
          .filter((n) => Number(n.dataset.btn) === 17)
          .forEach((node) => {
            node.classList.add('is-active', 'is-tested');
          });
        const check = this.querySelector('[data-check="17"]');
        if (check) check.classList.add('is-tested', 'is-active');
      } else {
        this.els.btnNodes
          .filter((n) => Number(n.dataset.btn) === 17)
          .forEach((node) => {
            node.classList.toggle('is-active', false);
            node.classList.toggle('is-tested', this.testedButtons.has(17));
          });
        const check = this.querySelector('[data-check="17"]');
        if (check) {
          check.classList.toggle('is-active', false);
          check.classList.toggle('is-tested', this.testedButtons.has(17));
        }
      }

      const clickOk = this.testedButtons.has(17);
      const hasClickBtn =
        this.hid.active ||
        (pad.buttons && pad.buttons.length > 17) ||
        this._looksLikeSony(pad);

      if (source === 'webhid' || (source && source !== 'touched')) {
        state.surfaceSeen = true;
        state.waitMs = 0;
      } else if (hasClickBtn && !state.surfaceSeen) {
        state.waitMs += dt;
      }

      fingers.forEach((f) => {
        if (f.approximate) return;
        const gx = Math.min(3, Math.max(0, Math.floor(((f.x + 1) / 2) * 4)));
        const gy = Math.min(2, Math.max(0, Math.floor(((f.y + 1) / 2) * 3)));
        state.cells.add(`${gx},${gy}`);
        this.trails.touchpad.push({ x: f.x, y: f.y, t: now, id: f.id });
      });
      while (
        this.trails.touchpad.length > 240 ||
        (this.trails.touchpad[0] && now - this.trails.touchpad[0].t > 2800)
      ) {
        this.trails.touchpad.shift();
      }

      if (state.cells.size >= 5) state.surfaceOk = true;
      state.lastFingers = fingers;

      let status = 'pending';
      if (state.surfaceOk && clickOk) status = 'ok';
      else if (state.surfaceOk && !clickOk) status = 'move';
      else if (fingers.length || (state.surfaceSeen && !state.surfaceOk)) status = 'move';
      else if (clickOk && state.waitMs > 2500 && !state.surfaceSeen) status = 'click-only';
      else if (!state.surfaceSeen && state.waitMs > 4000 && hasClickBtn && !this.hid.active) {
        status = 'unsupported';
      } else if (clickOk) status = 'move';

      this._setTouchpadUi(status, fingers);
      this._drawTouchpad(fingers);
      this._updateSvgFingers(fingers);

      if (this.els.touchpadClickCheck) {
        this.els.touchpadClickCheck.classList.toggle('is-tested', clickOk);
        this.els.touchpadClickCheck.classList.toggle('is-active', clickActive);
      }
      if (this.els.touchpadSurfaceCheck) {
        this.els.touchpadSurfaceCheck.classList.toggle('is-tested', state.surfaceOk);
        this.els.touchpadSurfaceCheck.classList.toggle('is-active', fingers.length > 0);
        this.els.touchpadSurfaceCheck.hidden = status === 'unsupported' && !state.surfaceSeen;
      }
    }

    async _connectHid() {
      if (!navigator.hid) {
        if (this.els.touchpadNote) this.els.touchpadNote.textContent = this.i18n.touchpadHidUnsupported;
        return;
      }

      if (this.hid.active) {
        await this._closeHid();
        if (this.els.touchpadHidBtn) {
          this.els.touchpadHidBtn.classList.remove('is-active');
          this.els.touchpadHidBtn.textContent = this.i18n.touchpadHid;
        }
        this._setHidNote(
          'Stäng Steam/DS4Windows helt, använd USB-datakabel + Chrome/Edge, sedan Aktivera touchpad (USB).'
        );
        this._hidLog('WebHID frånkopplad');
        return;
      }

      // Must target the Generic Desktop Gamepad collection — otherwise Windows
      // may hand us a vendor/audio interface without touch/feature reports.
      const filters = [
        { vendorId: 0x054c, productId: 0x0ce6, usagePage: 0x01, usage: 0x05 },
        { vendorId: 0x054c, productId: 0x0df2, usagePage: 0x01, usage: 0x05 },
        { vendorId: 0x054c, productId: 0x0ce4, usagePage: 0x01, usage: 0x05 },
      ];

      try {
        this._hidLog('Väntar på enhetsval…');
        const devices = await navigator.hid.requestDevice({ filters });
        const device = devices[0];
        if (!device) {
          this._hidLog('Inget valt i listan');
          return;
        }

        if (!device.opened) await device.open();
        if (!device.opened) throw new Error('open-failed');

        this.hid.device = device;
        this.hid.active = true;
        this.hid.fingers = [];
        this.hid.click = false;
        this.hid.reportCount = 0;
        this.hid.touchReportCount = 0;
        this.hid.lastReportId = null;
        this.hid.wireless = this._detectWireless(device);

        this._hidLog(
          `Öppen: ${device.productName || 'Sony'} PID 0x${device.productId
            .toString(16)
            .padStart(4, '0')} · ${this.hid.wireless ? 'BT' : 'USB'}`
        );

        // Enables extended reports on BT; also pulls IMU calibration
        try {
          await device.receiveFeatureReport(0x05);
        } catch {
          /* USB may ignore / already enabled */
        }

        device.addEventListener('inputreport', this._onHidReport);
        await this._readDualSenseIdentity(device);

        if (this.els.touchpadHidBtn) {
          this.els.touchpadHidBtn.classList.add('is-active');
          this.els.touchpadHidBtn.textContent = this.i18n.touchpadHidActive;
        }

        const id = this.deviceIdentity;
        if (id && id.board) {
          this._setHidNote(`WebHID OK · ${id.summary}. Dra på touchpaden.`);
        } else if (id && id.firmware) {
          this._setHidNote(
            `WebHID OK · FW ${id.firmware}. BDM saknas — klistra in serienummer från QR nedan, eller stäng Steam/DS4Windows.`
          );
        } else {
          this._setHidNote(
            this.hid.wireless
              ? 'WebHID aktiv (Bluetooth). Dra på touchpaden. Om inget: USB + stäng Steam/DS4Windows.'
              : 'WebHID aktiv (USB). Dra på touchpaden. Serienummer → BDM via QR om auto misslyckas.'
          );
        }

        // If no reports after 2s, device is likely locked by another app
        window.setTimeout(() => {
          if (!this.hid.active) return;
          if ((this.hid.reportCount || 0) === 0) {
            this._hidLog('Inga inputreport – Steam/DS4Windows/DualSenseX håller troligen enheten');
            this._setHidNote(
              'WebHID öppnad men ingen data. Stäng Steam helt (även tray), DS4Windows och DualSenseX. Koppla om USB.'
            );
          }
        }, 2000);

        this.touchpadState.waitMs = 0;
      } catch (err) {
        console.warn('DualSense WebHID', err);
        this._hidLog(`Fel: ${err && err.name ? err.name : 'error'} ${err && err.message ? err.message : ''}`);
        const msg =
          err && err.name === 'NotAllowedError'
            ? 'WebHID nekades. Tillåt enheten i Chrome, eller stäng Steam/DS4Windows och försök igen.'
            : err && String(err.message || '').includes('open')
              ? 'Kunde inte öppna HID. Stäng Steam/DS4Windows (exklusiv åtkomst) och koppla om USB.'
              : this.i18n.touchpadHidError;
        this._setHidNote(msg);
        await this._closeHid();
      }
    }

    _setHidNote(text) {
      if (this.els.touchpadNote) this.els.touchpadNote.textContent = text;
    }

    _hidLog(text) {
      if (!this.els.hidDebug) return;
      this.els.hidDebug.hidden = false;
      const prev = this.els.hidDebug.textContent ? `${this.els.hidDebug.textContent}\n` : '';
      const lines = `${prev}${text}`.split('\n').slice(-6);
      this.els.hidDebug.textContent = lines.join('\n');
    }

    _applySerial(raw) {
      const serial = String(raw || '')
        .trim()
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, '');
      if (serial.length < 6) {
        this._setHidNote('Serienummer för kort (minst 6 tecken från QR).');
        return;
      }

      const revisionChar = serial.charAt(1);
      const boardMap = {
        1: 'BDM-010',
        2: 'BDM-020',
        3: 'BDM-030',
        4: 'BDM-040',
        5: 'BDM-050',
        6: 'BDM-060',
      };
      const board = boardMap[revisionChar] || '';
      const colorCode = serial.slice(4, 6);
      const colorMap = {
        '00': 'White',
        '01': 'Midnight Black',
        '02': 'Cosmic Red',
        '03': 'Nova Pink',
        '04': 'Galactic Purple',
        '05': 'Starlight Blue',
      };
      const color = colorMap[colorCode] || '';

      if (!this.deviceIdentity) this.deviceIdentity = {};
      this.deviceIdentity.serial = serial;
      this.deviceIdentity.board = board;
      const model =
        this._sonyModelFromProductId(this.hid.device && this.hid.device.productId) || {
          family: 'DualSense',
        };
      const parts = [model.family];
      if (board) parts.push(this._bdmLabel(board));
      else parts.push(`rev '${revisionChar}' (okänd BDM)`);
      if (color) parts.push(color);
      if (this.deviceIdentity.firmware) parts.push(`FW ${this.deviceIdentity.firmware}`);
      this.deviceIdentity.summary = parts.join(' · ');

      this._hidLog(`Serienummer: ${serial}${board ? ` → ${board}` : ''}`);
      this._setHidNote(
        board
          ? `BDM från QR: ${this._bdmLabel(board)}${color ? ` · ${color}` : ''}`
          : `Serienummer sparat, men tecken 2 (“${revisionChar}”) matchar ingen känd BDM.`
      );
    }

    _detectWireless(device) {
      try {
        for (const c of device.collections || []) {
          if (c.usagePage !== 0x01 || c.usage !== 0x05) continue;
          const maxBits = (c.inputReports || []).reduce((max, report) => {
            const bits = (report.items || []).reduce(
              (sum, item) => sum + (item.reportSize || 0) * (item.reportCount || 0),
              0
            );
            return Math.max(max, bits);
          }, 0);
          if (maxBits === 504) return false;
          if (maxBits === 616) return true;
        }
      } catch {
        /* ignore */
      }
      return false;
    }

    _featureView(report) {
      if (report instanceof DataView) {
        return new DataView(report.buffer, report.byteOffset, report.byteLength);
      }
      return new DataView(report.buffer || report);
    }

    async _readDualSenseIdentity(device) {
      const model = this._sonyModelFromProductId(device.productId) || {
        family: device.productName || 'Sony',
        variant: '',
      };

      try {
        const view = this._featureView(await device.receiveFeatureReport(0x20));
        const hasId = view.byteLength >= 48 && view.getUint8(0) === 0x20;
        const base = hasId ? 1 : 0;

        const hardwareInfo = view.getUint32(base + 23, true) >>> 0;
        const firmwareRaw = view.getUint32(base + 27, true) >>> 0;
        const firmware = this._formatFwVersion(firmwareRaw);
        let buildDate = '';
        for (let i = 0; i < 11; i++) {
          const c = view.getUint8(base + i);
          if (c === 0) break;
          if (c >= 32 && c < 127) buildDate += String.fromCharCode(c);
        }

        this.deviceIdentity = {
          productId: device.productId,
          productName: device.productName || '',
          board: '',
          serial: '',
          firmware,
          firmwareRaw,
          hardwareInfo,
          buildDate: buildDate.trim(),
          summary: `${model.family} · FW ${firmware} · PID ${device.productId
            .toString(16)
            .padStart(4, '0')}`,
        };

        // BDM comes from serial (test cmd 0x80/0x81), not from report 0x20
        const factory = await this._readDualSenseSerial(device, hardwareInfo, firmwareRaw);
        if (factory) {
          this.deviceIdentity.serial = factory.serial;
          this.deviceIdentity.board = factory.board;
          this.deviceIdentity.color = factory.color;
          const parts = [model.family];
          if (factory.board) parts.push(this._bdmLabel(factory.board));
          if (factory.color) parts.push(factory.color);
          parts.push(`FW ${firmware}`);
          this.deviceIdentity.summary = parts.join(' · ');
        }
      } catch (err) {
        console.warn('DualSense identity', err);
        this.deviceIdentity = {
          productId: device.productId,
          summary: `${model.family} · PID ${device.productId.toString(16).padStart(4, '0')}`,
        };
      }
    }

    async _readDualSenseSerial(device, hardwareInfo, firmwareRaw) {
      // Gate from dualsense-ts / Sony test protocol — log when skipped but still try once
      const hwOk = (hardwareInfo & 0xffff) >= 777;
      const fwOk = firmwareRaw >= 65655;
      if (!hwOk || !fwOk) {
        this._hidLog(
          `Serial-gate: HW ${hardwareInfo & 0xffff} / FW ${firmwareRaw}` +
            ` (${hwOk ? 'HW ok' : 'HW låg'}, ${fwOk ? 'FW ok' : 'FW låg'}) — försöker ändå`
        );
      }

      try {
        const send = new Uint8Array(63);
        send[0] = 0x01; // device system
        send[1] = 0x13; // read serial
        await device.sendFeatureReport(0x80, send);

        for (let attempt = 0; attempt < 20; attempt++) {
          await new Promise((r) => setTimeout(r, 50));
          const view = this._featureView(await device.receiveFeatureReport(0x81));
          const offset = view.byteLength && view.getUint8(0) === 0x81 ? 1 : 0;
          if (view.byteLength < offset + 3) continue;
          const respDevice = view.getUint8(offset);
          const respAction = view.getUint8(offset + 1);
          const respStatus = view.getUint8(offset + 2);
          if (respDevice !== 0x01 || respAction !== 0x13) continue;
          if (respStatus !== 0x02) continue;

          let serial = '';
          for (let i = 0; i < 32; i++) {
            const c = view.getUint8(offset + 3 + i);
            if (!c) break;
            if (c >= 32 && c < 127) serial += String.fromCharCode(c);
          }
          if (serial.length < 6) {
            this._hidLog(`Serial-svar för kort (${serial.length} tecken)`);
            return null;
          }

          const revisionChar = serial.charAt(1);
          const boardMap = {
            1: 'BDM-010',
            2: 'BDM-020',
            3: 'BDM-030',
            4: 'BDM-040',
            5: 'BDM-050',
            6: 'BDM-060',
          };
          const colorCode = serial.slice(4, 6);
          const colorMap = {
            '00': 'White',
            '01': 'Midnight Black',
            '02': 'Cosmic Red',
            '03': 'Nova Pink',
            '04': 'Galactic Purple',
            '05': 'Starlight Blue',
          };

          this._hidLog(`Serial OK: ${serial} → ${boardMap[revisionChar] || '?'}`);
          return {
            serial,
            board: boardMap[revisionChar] || '',
            color: colorMap[colorCode] || '',
          };
        }
        this._hidLog('Serial: inget giltigt 0x81-svar (vanligt om Steam/BT/äldre FW)');
      } catch (err) {
        console.warn('DualSense serial', err);
        this._hidLog(`Serial-fel: ${err && err.message ? err.message : 'feature report'}`);
      }
      return null;
    }

    _onHidReport = (event) => {
      const reportId = event.reportId;
      const src = event.data;
      const data = new DataView(src.buffer, src.byteOffset, src.byteLength);
      this.hid.reportCount = (this.hid.reportCount || 0) + 1;
      this.hid.lastReportId = reportId;

      if (this.hid.reportCount === 1) {
        this._hidLog(`Första report 0x${reportId.toString(16)} (${data.byteLength} B)`);
      }

      let parsed = null;
      if (reportId === 0x01 && data.byteLength >= 40) {
        parsed = this._parseDualSenseTouch(data, 32, 9);
      } else if (reportId === 0x31 && data.byteLength >= 42) {
        parsed = this._parseDualSenseTouch(data, 33, 10);
      }

      if (!parsed) {
        if (this.hid.reportCount === 1) {
          this._hidLog('Reportformat matchar inte USB/BT touch — fel collection eller driver?');
        }
        return;
      }
      this.hid.click = parsed.click;
      this.hid.fingers = parsed.fingers;
      if (parsed.click) this.testedButtons.add(17);

      this.hid.touchReportCount = (this.hid.touchReportCount || 0) + 1;
      if (this.hid.touchReportCount === 1) {
        this._hidLog(
          `Touch OK · 0x${reportId.toString(16)}` +
            (parsed.fingers.length ? ` · ${parsed.fingers.length} finger` : '')
        );
      }
      if (this.hid.touchReportCount === 1 || this.hid.touchReportCount % 120 === 0) {
        this._setHidNote(
          `WebHID OK · report 0x${reportId.toString(16)} · ${data.byteLength} B` +
            (parsed.fingers.length ? ` · ${parsed.fingers.length} finger` : '')
        );
      }
    };

    _parseDualSenseTouch(data, touchOffset, buttons2Offset) {
      const buttons2 = data.getUint8(buttons2Offset);
      const click = Boolean(buttons2 & 0x02);
      const fingers = [];
      const width = 1920;
      const height = 1080;

      for (let i = 0; i < 2; i++) {
        const base = touchOffset + i * 4;
        const b0 = data.getUint8(base);
        const b1 = data.getUint8(base + 1);
        const b2 = data.getUint8(base + 2);
        const b3 = data.getUint8(base + 3);
        const active = !(b0 & 0x80);
        if (!active) continue;
        const rawX = ((b2 & 0x0f) << 8) | b1;
        const rawY = (b3 << 4) | ((b2 & 0xf0) >> 4);
        const x = (rawX / (width - 1)) * 2 - 1;
        const y = (rawY / (height - 1)) * 2 - 1;
        fingers.push({
          x: Math.max(-1, Math.min(1, x)),
          y: Math.max(-1, Math.min(1, y)),
          id: b0 & 0x7f,
        });
      }

      return { click, fingers };
    }

    async _closeHid() {
      if (this.hid.device) {
        try {
          this.hid.device.removeEventListener('inputreport', this._onHidReport);
          if (this.hid.device.opened) await this.hid.device.close();
        } catch {
          /* ignore */
        }
      }
      this.hid.device = null;
      this.hid.active = false;
      this.hid.click = false;
      this.hid.fingers = [];
      this.deviceIdentity = null;
    }

    _setTouchpadUi(status, fingers) {
      const badge = this.els.touchpadBadge;
      const card = this.els.touchpadCard;
      const info = this.els.touchpadInfo;
      if (card) {
        card.classList.toggle('is-ok', status === 'ok');
        card.classList.toggle('is-warn', status === 'click-only' || status === 'unsupported');
      }
      if (badge) {
        const map = {
          pending: this.i18n.touchpadPending,
          move: this.i18n.touchpadMove,
          ok: this.i18n.touchpadOk,
          'click-only': this.i18n.touchpadClickOnly,
          unsupported: this.i18n.touchpadUnsupported,
        };
        badge.className = `gamepad-tester__badge is-${status === 'click-only' ? 'move' : status === 'unsupported' ? 'drift' : status}`;
        badge.textContent = map[status] || map.pending;
      }
      if (info) {
        if (fingers.length) {
          const pct = Math.min(100, Math.round((this.touchpadState.cells.size / 12) * 100));
          info.textContent = this.i18n.touchpadFingers
            .replace('{{count}}', String(fingers.length))
            .replace('{{pct}}', String(pct));
        } else if (status === 'unsupported') {
          info.textContent = this.i18n.touchpadUnsupported;
        } else if (status === 'click-only') {
          info.textContent = this.i18n.touchpadClickOnly;
        } else {
          info.textContent = 'Tryck ner + dra fingret över touchpaden';
        }
      }
    }

    _updateSvgFingers(fingers) {
      // Touchpad bounds match silhouette pad (~43–85 × ~29–52)
      const left = 45;
      const top = 31;
      const width = 38;
      const height = 19;
      this.els.touchDots.forEach((dot, i) => {
        const f = fingers[i];
        if (!f) {
          dot.setAttribute('hidden', '');
          return;
        }
        dot.removeAttribute('hidden');
        const nx = (f.x + 1) / 2;
        const ny = (f.y + 1) / 2;
        // Two fingers: nudge slightly apart around pad center when both near middle
        let offsetX = 0;
        if (fingers.length > 1) {
          offsetX = i === 0 ? -4 : 4;
        }
        const cx = left + Math.max(0.08, Math.min(0.92, nx)) * width + offsetX;
        const cy = top + Math.max(0.12, Math.min(0.88, ny)) * height;
        dot.setAttribute('cx', String(cx));
        dot.setAttribute('cy', String(cy));
      });
    }

    _drawTouchpad(fingers) {
      const canvas = this.els.touchpadCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const w = canvas.width;
      const h = canvas.height;
      const pad = 12;
      const rw = w - pad * 2;
      const rh = h - pad * 2;

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#fff';
      ctx.strokeStyle = 'rgba(18,18,18,0.16)';
      ctx.lineWidth = 2;
      this._roundRect(ctx, pad, pad, rw, rh, 12);
      ctx.fill();
      ctx.stroke();

      // Coverage cells
      const cols = 4;
      const rows = 3;
      for (let gy = 0; gy < rows; gy++) {
        for (let gx = 0; gx < cols; gx++) {
          if (!this.touchpadState.cells.has(`${gx},${gy}`)) continue;
          const cw = rw / cols;
          const ch = rh / rows;
          ctx.fillStyle = 'rgba(198,255,77,0.28)';
          ctx.fillRect(pad + gx * cw, pad + gy * ch, cw, ch);
        }
      }

      // Trail
      const trail = this.trails.touchpad;
      if (trail.length > 1) {
        const byId = new Map();
        trail.forEach((p) => {
          if (!byId.has(p.id)) byId.set(p.id, []);
          byId.get(p.id).push(p);
        });
        byId.forEach((pts) => {
          ctx.beginPath();
          pts.forEach((p, i) => {
            const px = pad + ((p.x + 1) / 2) * rw;
            const py = pad + ((p.y + 1) / 2) * rh;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          });
          ctx.strokeStyle = 'rgba(51,51,51,0.35)';
          ctx.lineWidth = 2;
          ctx.stroke();
        });
      }

      const colors = ['#333333', '#c6ff4d'];
      fingers.forEach((f, i) => {
        const px = pad + ((f.x + 1) / 2) * rw;
        const py = pad + ((f.y + 1) / 2) * rh;
        ctx.beginPath();
        ctx.arc(px, py, 10, 0, Math.PI * 2);
        ctx.fillStyle = colors[i % colors.length];
        ctx.fill();
        ctx.strokeStyle = '#121212';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });
    }

    _roundRect(ctx, x, y, w, h, r) {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }

    _updateStick(side, x, y, dt, now) {
      const mag = Math.hypot(x, y);
      const state = this.stickState[side];
      state.lastX = x;
      state.lastY = y;
      state.maxMag = Math.max(state.maxMag, mag);

      if (mag > 0.55) state.rangeOk = true;

      const inDeadzone = mag <= this.driftThreshold;
      const frameDelta = Math.hypot(x - (state._px || 0), y - (state._py || 0));
      const nearlyStill = frameDelta < 0.015;
      state._px = x;
      state._py = y;

      // Resting off-center → classic drift (red)
      if (!inDeadzone && nearlyStill && mag > this.driftThreshold) {
        state.restOffMs += dt;
      } else if (inDeadzone) {
        state.restOffMs = 0;
      } else {
        state.restOffMs = Math.max(0, state.restOffMs - dt * 0.35);
      }

      // Bounce: holding still in one direction, but stick jitters/studsar.
      // Not sticky — clears when stable again or released.
      const angle = Math.atan2(y, x);
      let angleDelta = 0;
      if (state._angle != null) {
        angleDelta = Math.abs(angle - state._angle);
        if (angleDelta > Math.PI) angleDelta = Math.PI * 2 - angleDelta;
      }
      state._angle = angle;

      const heldOut = mag >= 0.65;
      const sameDirection = angleDelta < 0.28; // ~16°

      if (heldOut && sameDirection) {
        state.holdDirMs += dt;
        // Only measure jitter after clearly holding a direction
        if (state.holdDirMs > 500) {
          if (frameDelta > 0.045) {
            state.jitterMs += dt;
          } else {
            state.jitterMs = Math.max(0, state.jitterMs - dt * 0.7);
          }
        }
        state.bounceActive = state.holdDirMs >= 1100 && state.jitterMs >= 380;
      } else {
        state.holdDirMs = Math.max(0, state.holdDirMs - dt * 2.5);
        state.jitterMs = Math.max(0, state.jitterMs - dt * 2.5);
        if (state.holdDirMs < 400 || state.jitterMs < 200) {
          state.bounceActive = false;
        }
      }

      let status = 'pending';
      if (state.restOffMs > 700) {
        status = 'drift';
      } else if (state.bounceActive) {
        status = 'bounce';
      } else if (state.rangeOk && inDeadzone) {
        status = 'ok';
      } else if (state.maxMag > 0.2 || mag > 0.12) {
        status = 'move';
      }

      // Sticky OK / drift only — bounce is live and clears on its own
      if (state.status === 'ok' && status !== 'drift' && status !== 'bounce') {
        status = 'ok';
      }
      if (state.status === 'drift' && inDeadzone && state.rangeOk) {
        status = 'ok';
        state.restOffMs = 0;
      } else if (state.status === 'drift' && status !== 'ok') {
        status = 'drift';
      }

      state.status = status;

      if (mag > 0.04) {
        const trail = this.trails[side];
        trail.push({ x, y, t: now });
        while (trail.length > 180 || (trail[0] && now - trail[0].t > 2500)) trail.shift();
      }

      this._setStickUi(side, status);
      this._moveKnob(side, x, y);
      this._drawStickPad(side, x, y);
      if (this.els.stickXy[side]) {
        this.els.stickXy[side].textContent = `X ${x.toFixed(2)} · Y ${y.toFixed(2)}`;
      }
    }

    _setStickUi(side, status) {
      const card = this.els.stickCards[side];
      const badge = this.els.stickBadges[side];
      if (card) {
        card.classList.toggle('is-ok', status === 'ok');
        card.classList.toggle('is-bounce', status === 'bounce');
        card.classList.toggle('is-drift', status === 'drift');
      }
      if (badge) {
        badge.className = `gamepad-tester__badge is-${status}`;
        const map = {
          pending: this.i18n.stickPending,
          move: this.i18n.stickMove,
          ok: this.i18n.stickOk,
          bounce: this.i18n.stickBounce,
          drift: this.i18n.stickDrift,
        };
        badge.textContent = map[status] || map.pending;
      }
    }

    _moveKnob(side, x, y) {
      const knob = this.els.stickKnobs[side];
      if (!knob) return;
      const baseX = Number(knob.dataset.baseX);
      const baseY = Number(knob.dataset.baseY);
      const base = {
        x: Number.isFinite(baseX) ? baseX : side === 'left' ? 45.5 : 82.5,
        y: Number.isFinite(baseY) ? baseY : 64.4632,
      };
      const reach = 3.2;
      const nx = Math.max(-1, Math.min(1, x));
      const ny = Math.max(-1, Math.min(1, y));
      knob.setAttribute('transform', `translate(${base.x + nx * reach} ${base.y + ny * reach})`);
    }

    _drawStickPad(side, x, y) {
      const canvas = this.els.canvases[side];
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const w = canvas.width;
      const h = canvas.height;
      const cx = w / 2;
      const cy = h / 2;
      const r = w * 0.42;

      ctx.clearRect(0, 0, w, h);

      // Outer ring
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(18,18,18,0.16)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Unit circle guide
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(18,18,18,0.06)';
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Deadzone
      const dz = r * this.driftThreshold;
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(6, dz), 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(198,255,77,0.12)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(18,18,18,0.18)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Crosshair
      ctx.beginPath();
      ctx.moveTo(cx - r, cy);
      ctx.lineTo(cx + r, cy);
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx, cy + r);
      ctx.strokeStyle = 'rgba(18,18,18,0.08)';
      ctx.stroke();

      // Trail
      const trail = this.trails[side];
      if (trail.length > 1) {
        ctx.beginPath();
        trail.forEach((p, i) => {
          const px = cx + p.x * r;
          const py = cy + p.y * r;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.strokeStyle = 'rgba(51,51,51,0.35)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Knob
      const kx = cx + Math.max(-1, Math.min(1, x)) * r;
      const ky = cy + Math.max(-1, Math.min(1, y)) * r;
      const status = this.stickState[side].status;
      const fill =
        status === 'ok'
          ? '#c6ff4d'
          : status === 'bounce'
            ? '#f7931a'
            : status === 'drift'
              ? '#e24b4b'
              : '#333333';

      ctx.beginPath();
      ctx.arc(kx, ky, 9, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.strokeStyle = '#121212';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    _requiredButtons(pad) {
      const list = this.buttonDefs
        .map((d) => d.index)
        .filter((i) => {
          if (i === 17) {
            return pad.buttons.length > 17 || this.testedButtons.has(17) || this._looksLikeSony(pad);
          }
          return i < pad.buttons.length || this.testedButtons.has(i);
        });
      return list.length ? list : this.buttonDefs.slice(0, 16).map((d) => d.index);
    }

    _updateProgress() {
      const pad = this._pads()[this.activeSlot];
      const required = pad ? this._requiredButtons(pad) : this.buttonDefs.slice(0, 16).map((d) => d.index);
      const testedCount = required.filter((i) => this.testedButtons.has(i)).length;
      const sticksOk =
        (this.stickState.left.status === 'ok' ? 1 : 0) +
        (this.stickState.right.status === 'ok' ? 1 : 0);

      const wantsTouchpad = Boolean(pad && required.includes(17));
      const surfaceUnavailable =
        wantsTouchpad && !this.touchpadState.surfaceSeen && this.touchpadState.waitMs > 4000;
      const surfaceUnit = wantsTouchpad && !surfaceUnavailable ? 1 : 0;
      const surfaceDone = this.touchpadState.surfaceOk ? 1 : 0;

      const totalUnits = required.length + 2 + surfaceUnit;
      const doneUnits = testedCount + sticksOk + surfaceDone;
      const pct = totalUnits ? Math.round((doneUnits / totalUnits) * 100) : 0;

      if (this.els.progressFill) this.els.progressFill.style.width = `${pct}%`;

      if (this.els.progressText) {
        const clickOk = !wantsTouchpad || this.testedButtons.has(17);
        const surfaceOk = !wantsTouchpad || this.touchpadState.surfaceOk || surfaceUnavailable;
        const allOk = testedCount >= required.length && sticksOk === 2 && clickOk && surfaceOk;
        const buttonsPart = this.i18n.buttonsDone
          .replace('{{tested}}', String(testedCount))
          .replace('{{total}}', String(required.length));
        const sticksPart = this.i18n.sticksDone.replace('{{ok}}', String(sticksOk));
        this.els.progressText.textContent = allOk
          ? this.i18n.allOk
          : `${buttonsPart} · ${sticksPart}`;
        this.els.progressText.classList.toggle('is-complete', allOk);
      }
    }

    _vibrate(strength) {
      const pad = this._pads()[this.activeSlot];
      const note = this.els.vibrateNote;
      if (!pad) return;

      const actuator =
        pad.vibrationActuator ||
        (pad.hapticActuators && pad.hapticActuators[0]) ||
        null;

      if (!actuator || typeof actuator.playEffect !== 'function') {
        if (note) {
          note.hidden = false;
          note.textContent = this.i18n.vibrateUnsupported;
        }
        return;
      }

      if (note) note.hidden = true;
      actuator
        .playEffect('dual-rumble', {
          startDelay: 0,
          duration: 420,
          weakMagnitude: strength * 0.7,
          strongMagnitude: strength,
        })
        .catch(() => {
          if (note) {
            note.hidden = false;
            note.textContent = this.i18n.vibrateUnsupported;
          }
        });
    }
  }

  customElements.define('gamepad-tester', GamepadTester);
}
